// New SpamOrdersPage.js - copied from NewOrdersPage, adjusted
import React, { useState, useEffect } from "react";
import { fetchCanceledOrders, updateOrderStatus, deleteOrder } from "../../../api/OrderAPIs";
import {checkReturnMatch} from "../../../api/ReturnsAPIs";
import { fetchDesignById } from "../../../api/DesignAPIs";
import { fetchTypeById } from "../../../api/TypeAPIs";
import { useAuth } from "../../../utils/auth";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../../../api/URL_Header";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const CanceledOrders = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const [loadedOrders, setLoadedOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (authState.role !== "Admin" && authState.role !== "Moderator") {
      navigate("/login");
      return;
    }
    loadInitialOrders();
  }, [authState, navigate]);

  const handleDownload = async (url, filename = "design.jpg") => {
    try {
      const response = await fetch(
        `${API_BASE_URL_V2}/api/files/download?url=${encodeURIComponent(url)}&filename=${encodeURIComponent(filename)}`
      );

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
      alert("⚠️ فشل التحميل. تحقق من السيرفر.");
    }
  };

  const enrichOrders = async (data) => {
    return await Promise.all(
      data.map(async (order) => {
        const enrichedItems = await Promise.all(
          order.orderDetails.map(async (detail) => {
            const design = await fetchDesignById(detail.designId);
            const type = await fetchTypeById(detail.typeId);
            if (!design || !type) return null;
            return {
              id: detail.designId,
              name: design.Name,
              quantity: detail.quantity,
              price: detail.price,
              type: type.name,
              driveLink: design.code.startsWith("CUSTOM") ? `${API_BASE_URL_V2}${design.driveLink}` : design.driveLink || "",
              code: design.code || "None",
            };
          })
        ).then((items) => items.filter((item) => item !== null));



        return {
          orderNumber: order.orderId,
          items: enrichedItems,
          total: order.totalAmount,
          profit: order.profit || 0,
          date: new Date(order.date).toISOString().split("T")[0],
          address: order.address?.fullAddress || "Unknown Address",
          state: order.address?.state || "Unknown Address",
          phoneNumber: order.phoneNumber || "Unknown Phone",
          phoneNumber2: order.phoneNumber2 || "Unknown Phone",
          name: order.name || "Unknown Customer",
          note: order.note || "",
          status: order.status || "Spam",
        };
      })
    );
  };

  const loadInitialOrders = async () => {
    setIsOrdersLoading(true);
    try {
      const data = await fetchCanceledOrders(0, 10);
      if (data.length === 0 || data.length < 10) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
    } catch (err) {
      console.error("Error loading initial orders:", err);
      setError("فشل في تحميل الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل الطلبات!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    setIsLoadingMore(true);
    try {
      const data = await fetchCanceledOrders(currentPage, 10);
      if (data.length === 0 || data.length < 10) {
        setHasMore(false);
      }
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders((prev) => [...prev, ...enrichedOrders]);
      setCurrentPage((prev) => prev + 1);
    } catch (err) {
      console.error("Error loading more orders:", err);
      setError("فشل في تحميل المزيد من الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل المزيد من الطلبات!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    let reason = "no reason (normal order)";
    if (newStatus === "Hold") {
      reason = window.prompt("يرجى إدخال سبب وضع الطلب على الانتظار (مثل: لا يوجد عنوان، مؤجل إلى تاريخ، إلخ):");
      if (reason === null) return; // User canceled the prompt
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب لوضع الطلب على الانتظار.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        return;
      }
    } else if (newStatus === "Changes") {
      reason = window.prompt("يرجى إدخال سبب التغيير (سيتم إلحاقه بالملاحظة الحالية):");
      if (reason === null) return; // User canceled the prompt
      if (!reason.trim()) {
        toast.error("يجب إدخال سبب التغيير.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
        return;
      }
    }

    const confirmUpdate = window.confirm(`هل أنت متأكد من تحديث حالة الطلب رقم ${orderId} إلى ${newStatus}${reason ? ` بسبب: ${reason}` : ''}؟`);
    if (!confirmUpdate) return;

    try {
      console.log(`Updating order ${orderId} to status ${newStatus} with reason: ${reason}`);
      await updateOrderStatus(orderId, newStatus, reason);
      setLoadedOrders((prev) =>
        prev.filter((order) => order.orderNumber !== orderId)
      );
      toast.success(`تم تحديث حالة الطلب رقم ${orderId} إلى ${newStatus} بنجاح!`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (err) {
      console.error("Error updating order status:", err);
      let errorMessage = "فشل في تحديث حالة الطلب. يرجى المحاولة مرة أخرى.";
      if (err.message.includes("401")) {
        errorMessage = "فشل في التحقق من الهوية. يرجى تسجيل الدخول مرة أخرى.";
      } else if (err.message.includes("400")) {
        errorMessage = "خطأ في البيانات المرسلة. تأكد من إدخال سبب صالح.";
      }
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (authState.role !== "Admin" && authState.role !== "Moderator") return;
    const confirmDelete = window.confirm(`هل أنت متأكد من حذف الطلب رقم ${orderId}؟`);
    if (!confirmDelete) return;
    try {
      await deleteOrder(orderId);
      setLoadedOrders((prev) =>
        prev.filter((order) => order.orderNumber !== orderId)
      );
      toast.success(`تم حذف الطلب رقم ${orderId} بنجاح!`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } catch (err) {
      console.error("Error deleting order:", err);
      let errorMessage = "فشل في حذف الطلب. يرجى المحاولة مرة أخرى.";
      setError(errorMessage);
      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  const handleCopyOrderInfo = (order) => {
    const phone = `${order.phoneNumber}-${order.phoneNumber2}`;
    const formattedState = `${order.state} (D)`;
    const text = `${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD`;

    navigator.clipboard.writeText(text).then(() => {
      toast.success("تم نسخ معلومات العميل إلى الحافظة!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }).catch((err) => {
      console.error("Failed to copy:", err);
      toast.error("فشل في النسخ. جرب مرة أخرى.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    });
  };

  const handleCopyItems = (order) => {
    const lines = order.items.map((item, index) => {
      const itemNum = `${index + 1}-${order.items.length}`;
      const type = item.type || "Unknown Type";
      const driveLink = item.driveLink || "No Link";
      return `${itemNum}\t\tPhone Cover (H)\t${type}\t\t1\t${driveLink}`;
    }).join('\n');

    navigator.clipboard.writeText(lines).then(() => {
      toast.success("تم نسخ المنتجات إلى الحافظة!", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }).catch((err) => {
      console.error("Failed to copy:", err);
      toast.error("فشل في النسخ. جرب مرة أخرى.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6" dir="rtl">
      <ToastContainer />
      <div>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-lg font-semibold">الطلبات اللي هتتلغي</h2>
          <button
            onClick={() => navigate("/dashboard")}
            className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
          >
            رجوع
          </button>
        </div>
        {error && (
          <div className="bg-red-100 text-red-700 p-4 rounded mb-6">
            {error}
          </div>
        )}
        <div className="bg-white shadow-md rounded-lg p-6">
          {isOrdersLoading ? (
            <SkeletonLoader type="table" />
          ) : (
            <>
              {loadedOrders.length > 0 ? (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full table-auto">
                      <thead>
                        <tr className="bg-gray-100">
                          <th className="p-1 text-right font-semibold">رقم الطلب</th>
                          <th className="p-1 text-right font-semibold">اسم العميل</th>
                          <th className="p-1 text-right font-semibold">رقم الهاتف</th>
                          <th className="p-1 text-right font-semibold">العنوان</th>
                          <th className="p-1 text-right font-semibold">محافظه</th>
                          <th className="p-1 text-right font-semibold">الإجمالي</th>
                          <th className="p-1 text-right font-semibold">رقم</th>
                          <th className="p-1 text-right font-semibold">المنتجات</th>
                          <th className="p-1 text-right font-semibold">رابط الدرايف</th>
                          <th className="p-1 text-right font-semibold">ملاحظة</th>
                          <th className="p-1 text-right font-semibold min-w-[80px]">حذف</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadedOrders.map((order) => (
                          <tr key={order.orderNumber} className="border-b hover:bg-gray-100">
                            <td className="p-2 text-right">{order.orderNumber}</td>
                            <td className="p-2 text-right">{order.name}</td>
                            <td className="p-2 text-right">{order.phoneNumber}-{order.phoneNumber2}</td>
                            <td className="p-2 text-right">{order.address}</td>
                            <td className="p-2 text-right">{order.state}</td>
                            <td className="p-2 text-right">{order.total}</td>
                            <td className="p-2 text-right">
                              {order.items.map((item, index) => (
                                <div key={item.id} className="mb-2">
                                  {index + 1}-{order.items.length}
                                </div>
                              ))}
                            </td>
                            <td className="p-2 text-right">
                              {order.items.map((item) => (
                                <div key={item.id} className="mb-2">
                                  {item.name} {item.type}
                                  <p>----</p> 
                                </div>
                              ))}
                            </td>
                            
                            <td className="p-2 text-right">
                              {order.items.map((item) => (
                                <div key={item.id} className="mb-2">
                                  {item.driveLink ? (
                                    <>
                                      <a
                                        href={item.driveLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-500 hover:underline"
                                      >
                                        رابط الدرايف
                                      </a>
                                      <br />
                                      <span>{item.code}</span>
                                      {item.code.startsWith("CUSTOM") && (
                                          <>
                                            <button
                                              onClick={() => handleDownload(item.driveLink, `${order.orderNumber}.jpg`)}
                                              className="ml-2 bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
                                            >
                                              تحميل
                                            </button>
                                            <br />
                                          </>
                                        )}
                                    </>
                                  ) : (
                                    "لا يوجد رابط"
                                  )}
                                  <p>----</p>
                                </div>
                              ))}
                            </td>

                            <td className="p-2 text-xs">{order.note || "—"}</td>
                            
                            <td className="p-3 text-right">
                              {(authState.role === "Admin" || authState.role === "Moderator") && (
                                <button
                                  onClick={() => handleDeleteOrder(order.orderNumber)}
                                  className="bg-red-500 text-white rounded hover:bg-red-600 text-sm px-4 py-4 font-medium w-full"
                                  
                                >
                                  حذف
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {hasMore && (
                      <div className="text-center mt-4">
                        <button
                          onClick={loadMoreOrders}
                          disabled={isLoadingMore}
                          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
                        >
                          {isLoadingMore ? "جاري التحميل..." : "تحميل المزيد"}
                        </button>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <p className="text-center">لا توجد طلبات ملغيه</p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CanceledOrders;